import type { RewardsTableRowData } from '@/hooks/reward/useRewardsTable'

/**
 * Calculates the total sum of rewards in USD from the provided reward data.
 * Excludes rewards that have asset with no price.
 *
 * @param {RewardsTableRowData[]} rewardData - An array of reward data objects.
 * @returns {bigint} - The total sum of rewards in USD.
 */
export const calculateRewardSum = (rewardData: RewardsTableRowData[]): bigint => {
  return rewardData.reduce((acc, item) => {
    return acc + (item.claimable?.amountUsd ?? 0n)
  }, 0n)
}

/**
 * Whether every claimable reward carries a USD price.
 *
 * `calculateRewardSum` skips unpriced assets, which is right for display but
 * makes the total a lower bound — and 0 when nothing is priced at all. Callers
 * reporting that sum as an analytics value should omit it unless this returns
 * true, otherwise conversion-value averages are silently dragged down.
 */
export const isRewardSumFullyPriced = (rewardData: RewardsTableRowData[]): boolean =>
  // `!= null` rather than `!== undefined`: `Token.toUsd` signals "no price" with
  // `undefined`, but `amountUsd` is typed `Amount` (`bigint | null`), so both
  // nullish forms have to count as missing.
  rewardData.every(item => !item.claimable?.amount || item.claimable.amountUsd != null)
